import React from "react";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { T } from "services/lang/LanguageService";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { DashboardEditorProps } from "components/ComponentType";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { SankeyDiagramNode, SankeyDiagramProps } from "../sankey-types";

enum AxisType {
  X = "x",
  Y = "y",
}

const NodesEditor = ({ config, onConfigChange }: DashboardEditorProps<SankeyDiagramProps>) => {
  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={e => options.editorCallback?.(e.target.value as string)}
        style={{ width: "100%" }}
      />
    );
  };

  const xyPositionsTemplate = (options: ColumnBodyOptions) => {
    return (
      <div className="flex flex-row gap-2">
        <div>{config.nodesPosition?.x[options.rowIndex]}</div>
        <div>{config.nodesPosition?.y[options.rowIndex]}</div>
      </div>
    );
  };

  const renderXYPositionsEditor = (options: ColumnEditorOptions) => {
    const currentX = config.nodesPosition?.x?.[options.rowIndex] ?? 0;
    const currentY = config.nodesPosition?.y?.[options.rowIndex] ?? 0;

    const handlePositionChange = (e: InputNumberChangeEvent, axis: AxisType) => {
      const newposition = [...(config.nodesPosition?.[axis] ?? [])];
      newposition[options.rowIndex] = e.value ?? 0;

      if (axis === AxisType.X) {
        return onConfigChange({
          ...config,
          nodesPosition: {
            ...config.nodesPosition,
            x: newposition,
            y: config.nodesPosition?.y ?? [],
          },
        });
      }
      return onConfigChange({
        ...config,
        nodesPosition: {
          ...config.nodesPosition,
          x: config.nodesPosition?.x ?? [],
          y: newposition,
        },
      });
    };

    return (
      <div className="flex flex-row gap-2">
        <InputNumber
          value={currentX}
          maxFractionDigits={3}
          placeholder={AxisType.X}
          onChange={e => handlePositionChange(e, AxisType.X)}
        />
        <InputNumber
          value={currentY}
          maxFractionDigits={3}
          placeholder={AxisType.Y}
          onChange={e => handlePositionChange(e, AxisType.Y)}
        />
      </div>
    );
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const nodesToUpdate = [...config.graph.nodes!];
    const { newData } = e;
    const updatedNodes = nodesToUpdate.map(node => {
      if (node.id === newData.id) {
        return { ...node, ...newData };
      }
      return node;
    });

    const updatedConfig = {
      ...config,
      graph: { ...config.graph, nodes: updatedNodes },
    };
    onConfigChange(updatedConfig);
  };

  const renderReferenceCell = (rowData: SankeyDiagramNode) => {
    return (
      <InputSwitch
        checked={Boolean(rowData.reference)}
        onChange={e => {
          const nodesToUpdate = [...config.graph.nodes!];
          const updatedNodes = nodesToUpdate.map(node => {
            if (node.id === rowData.id) {
              return { ...node, reference: e.value };
            }
            return node;
          });

          const updatedConfig = {
            ...config,
            graph: { ...config.graph, nodes: updatedNodes },
          };
          onConfigChange(updatedConfig);
        }}
      />
    );
  };

  return (
    <DataTable
      value={config.graph.nodes}
      editMode="row"
      onRowEditComplete={onRowEditComplete}
      dataKey="id"
      size="small"
    >
      <Column field="id" header="ID" style={{ width: "30%" }} />
      <Column field="title" header={T("word.title")} editor={textEditor} style={{ width: "35%" }} />
      <Column
        field="reference"
        header={T("word.reference")}
        body={renderReferenceCell}
        style={{ width: "10%" }}
      />
      <Column
        field="xyPositions"
        header={config.overridePositions ? `XY ${T("word.positions")}` : ""}
        body={(rowData, options) => xyPositionsTemplate(options)}
        editor={renderXYPositionsEditor}
        style={{ width: "15%" }}
      />
      <Column rowEditor style={{ width: "10%", textAlign: "end" }} />
    </DataTable>
  );
};

export default NodesEditor;
